import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3, Vector4 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';
import { PALETTE } from '@fisura/shared';
import { sidewalkTexture } from './textures.js';

/**
 * Street dressing for Av. Rivadavia (the avenue band, z∈[-9,9]): raised
 * sidewalks + yellow curbs, painted road markings (lane dashes, crosswalks,
 * "Av. Rivadavia"), trees along the sidewalks, and proper car/bus visuals on
 * the existing parked-car colliders. All decorative (non-colliding) except the
 * car bodies, which sit on the map's existing car-box colliders.
 */

const TOP_FRONT = new Vector4(0, 0, 1, 1);

export function buildStreet(scene: Scene): void {
  const groups = new Map<StandardMaterial, Mesh[]>();
  const cache = new Map<string, StandardMaterial>();
  const mat = (key: string, c: [number, number, number], opts?: { tex?: 'sidewalk'; emissive?: boolean }): StandardMaterial => {
    let m = cache.get(key);
    if (!m) {
      m = new StandardMaterial(`street-${key}`, scene);
      m.specularColor = new Color3(0.04, 0.04, 0.04);
      if (opts?.tex === 'sidewalk') m.diffuseTexture = sidewalkTexture(scene);
      else m.diffuseColor = new Color3(c[0], c[1], c[2]);
      if (opts?.emissive) m.emissiveColor = new Color3(c[0] * 0.7, c[1] * 0.7, c[2] * 0.7);
      cache.set(key, m);
    }
    return m;
  };
  const box = (
    m: StandardMaterial,
    cx: number,
    cy: number,
    cz: number,
    sx: number,
    sy: number,
    sz: number,
    faceUV?: Vector4[],
  ): Mesh => {
    const opts: Parameters<typeof MeshBuilder.CreateBox>[1] = { width: sx, height: sy, depth: sz };
    if (faceUV) {
      opts.wrap = true;
      opts.faceUV = faceUV;
    }
    const mesh = MeshBuilder.CreateBox('st', opts, scene);
    mesh.position = new Vector3(cx, cy, cz);
    mesh.material = m;
    const g = groups.get(m);
    if (g) g.push(mesh);
    else groups.set(m, [mesh]);
    return mesh;
  };

  // --- Sidewalks + curbs (north z[9,13.7], south mirror) ---
  const walkM = mat('walk', [0.6, 0.6, 0.62], { tex: 'sidewalk' });
  const curbM = mat('curb', PALETTE.mid); // neutral curb — no yellow
  const swUV = (len: number, depth: number): Vector4[] => {
    const top = new Vector4(0, 0, len / 3, depth / 3);
    return [TOP_FRONT, TOP_FRONT, TOP_FRONT, TOP_FRONT, top, top];
  };
  // Stop at the cross streets (La Plata x64..78, Río de Janeiro x-78..-64) so
  // the avenue sidewalk/curb don't overflow the intersections.
  const SW_LEN = 128; // x -64..64
  for (const s of [1, -1]) {
    box(walkM, 0, 0.06, s * 11.35, SW_LEN, 0.12, 4.7, swUV(SW_LEN, 4.7)); // sidewalk slab
    box(curbM, 0, 0.09, s * 9.05, SW_LEN + 4, 0.18, 0.3); // curb — wraps 2u into each corner (x±66)
  }

  // --- Road markings (white paint just above the asphalt) ---
  // The centre (z=0) is a planted median, so NO centre line there. Lanes run
  // either side of it; dashes mark the lane split within each side.
  const paintM = mat('paint', PALETTE.white);
  const crosswalkX = [-61, 61];
  const inCrosswalk = (x: number): boolean => crosswalkX.some((c) => Math.abs(x - c) < 4);
  for (const z of [-6, 6]) {
    for (let x = -84; x <= 84; x += 6) {
      if (inCrosswalk(x)) continue;
      box(paintM, x, 0.045, z, 3, 0.02, 0.22);
    }
  }
  // Crosswalks across the avenue at each intersection mouth, set in by the
  // corner buildings (stripes run along x, arranged across z; gap at the
  // planted median) — so they cross Rivadavia rather than spill into the
  // cross street.
  for (const cxw of crosswalkX) {
    for (let i = -6; i <= 6; i++) box(paintM, cxw, 0.045, i * 1.3, 4, 0.02, 0.7); // continuous
  }

  // --- Cross streets: center lane dashes + edge sidewalks/curbs (N-S), broken
  //     at the avenue intersection (z[-9,9]). ---
  const crossStreets: { xc: number; xw: number; xe: number }[] = [
    { xc: 71, xw: 65, xe: 77 }, // Av. La Plata (east)
    { xc: -71, xw: -77, xe: -65 }, // Río de Janeiro (west)
  ];
  for (const cs of crossStreets) {
    for (let z = -55; z <= 55; z += 6) {
      if (Math.abs(z) < 10) continue; // clear the avenue crossing
      box(paintM, cs.xc, 0.045, z, 0.22, 0.02, 3); // center dashes (run along z)
    }
    // Sidewalks run right up to the avenue (z±9) so the corners meet the
    // avenue sidewalk; the curb stops a touch short (±9.3) so it doesn't
    // overlap/z-fight the wrapped avenue curb.
    for (const [z0, z1, c0, c1] of [[9, 56, 9.3, 56], [-56, -9, -56, -9.3]] as [number, number, number, number][]) {
      for (const [xe_, sgn] of [[cs.xw, 1], [cs.xe, -1]] as [number, number][]) {
        box(walkM, xe_, 0.06, (z0 + z1) / 2, 2.0, 0.12, z1 - z0, swUV(2.0, z1 - z0)); // sidewalk
        box(curbM, xe_ + sgn * 1.05, 0.09, (c0 + c1) / 2, 0.3, 0.18, c1 - c0); // curb on the road side
      }
    }
  }
  // --- Trees along the sidewalks ---
  const trunkM = mat('trunk', PALETTE.ink); // dark bark (no brown)
  const leafM = mat('leaf', PALETTE.slate); // foliage as cool slate masses (no green)
  const leaf2M = mat('leaf2', PALETTE.mid);
  const blockers: [number, number][] = [
    // [x, z] kiosks/shelters to keep clear of
    [-20, -11.5], [2, -11.5], [40, -11.5], [-34, 10.75], [28, 10.75],
  ];
  const treeBlocked = (x: number, z: number): boolean =>
    blockers.some(([bx, bz]) => Math.abs(x - bx) < 5 && Math.sign(bz) === Math.sign(z));
  for (const s of [1, -1]) {
    for (let x = -56; x <= 56; x += 14) {
      // Near the curb (sidewalk spans z 9..13.7) so canopies clear the z±14
      // building frontages instead of clipping into them.
      const z = s * 10.0;
      if (treeBlocked(x, z)) continue;
      box(trunkM, x, 1.5, z, 0.4, 3, 0.4);
      // low-poly canopy: a couple of stacked low-res spheres
      const c1 = MeshBuilder.CreateSphere('leaf', { diameter: 2.8, segments: 4 }, scene);
      c1.position.set(x, 3.6, z);
      c1.material = leafM;
      (groups.get(leafM) ?? groups.set(leafM, []).get(leafM)!).push(c1);
      const c2 = MeshBuilder.CreateSphere('leaf', { diameter: 2.0, segments: 4 }, scene);
      c2.position.set(x + 0.5, 4.4, z - 0.3);
      c2.material = leaf2M;
      (groups.get(leaf2M) ?? groups.set(leaf2M, []).get(leaf2M)!).push(c2);
    }
  }

  // --- Median planters: shrubs on top so they read as planted, not green boxes ---
  for (const mx of [-50, -30, -10, 10, 30, 50]) {
    for (const dx of [-1.6, 0, 1.6]) {
      const sh = MeshBuilder.CreateSphere('shrub', { diameter: 1.7, segments: 4 }, scene);
      sh.position.set(mx + dx, 1.5, 0);
      sh.scaling.y = 0.7;
      sh.material = leafM;
      (groups.get(leafM) ?? groups.set(leafM, []).get(leafM)!).push(sh);
    }
  }

  // --- Cars + a city bus on the parked-vehicle colliders (map.ts) ---
  // Sized to the player (≈1.8u tall): cars ~4.8×2.0×1.5, bus ~12×2.6×3.0, and
  // kept clear of the x±61 crosswalks.
  const wheelM = mat('wheel', PALETTE.ink);
  const glassM = mat('glass', [0.16, 0.17, 0.22]);
  const trimM = mat('trim', PALETTE.ink);
  const headM = mat('headlight', PALETTE.accent, { emissive: true });
  const tailM = mat('taillight', PALETTE.accent, { emissive: true });
  // Vehicle paint — strictly palette greys (light → dark) so parked traffic
  // reads as neutral monochrome; the warm accent is reserved for the bus.
  const carColors: [number, number, number][] = [
    PALETTE.light, PALETTE.slate, PALETTE.mid, PALETTE.dark, PALETTE.light,
  ];
  const car = (cx: number, cz: number, ci: number): void => {
    const bodyM = mat(`car-${ci}`, carColors[ci % carColors.length]!);
    box(bodyM, cx, 0.62, cz, 4.8, 0.9, 2.0); // lower body
    box(bodyM, cx - 0.3, 1.45, cz, 2.7, 0.8, 1.85); // cabin
    // Window band: a clear 0.05u proud of the cabin on every side + shorter in
    // height, so its faces never sit coplanar with the body (no z-fighting).
    box(glassM, cx - 0.3, 1.5, cz, 2.8, 0.55, 1.95);
    box(trimM, cx, 0.32, cz, 4.86, 0.22, 2.08); // rocker/bumper trim (proud)
    box(headM, cx + 2.43, 0.72, cz, 0.12, 0.28, 1.5); // headlights (poke past the nose)
    box(tailM, cx - 2.43, 0.72, cz, 0.12, 0.28, 1.5); // taillights
    for (const wx of [-1.65, 1.65]) for (const wz of [-1.0, 1.0]) box(wheelM, cx + wx, 0.38, cz + wz, 1.1, 0.75, 0.45);
  };
  const northCars = [-8, 26, 44];
  const southCars = [-50, -34, -4, 10, 28];
  northCars.forEach((x, i) => car(x, 7.5, i));
  southCars.forEach((x, i) => car(x, -7.5, i + 1));
  // City bus parked on the north curb (matches its map.ts collider; clear of
  // the stairwell holes + crosswalks).
  {
    const BX = -30;
    const busBody = mat('bus', PALETTE.accent); // the city's lone warm accent
    const busBand = mat('busband', PALETTE.ink); // dark waist band
    box(busBody, BX, 1.7, 7.5, 12, 2.6, 2.5); // body
    box(busBand, BX, 1.05, 7.5, 12.06, 0.55, 2.62); // dark waist band (proud)
    box(busBand, BX, 2.95, 7.5, 12.06, 0.3, 2.62); // roof rail (proud)
    box(glassM, BX, 2.4, 7.5, 11.2, 0.85, 2.66); // window row (clearly proud of the body)
    box(glassM, BX + 6.08, 2.0, 7.5, 0.12, 1.3, 2.3); // windshield (poke past the nose)
    box(trimM, BX, 0.55, 7.5, 12.08, 0.4, 2.7); // skirt
    box(headM, BX + 6.1, 1.0, 7.5, 0.12, 0.3, 1.9); // headlights
    for (const wx of [-4.2, 4.2]) for (const wz of [-1.05, 1.05]) box(wheelM, BX + wx, 0.5, 7.5 + wz, 1.3, 0.95, 0.5);
  }

  for (const [m, meshes] of groups) {
    const merged = meshes.length === 1 ? meshes[0]! : Mesh.MergeMeshes(meshes, true, true);
    if (merged) {
      merged.material = m;
      merged.isPickable = false;
      merged.freezeWorldMatrix();
    }
  }
}
